import { useApolloClient, useQuery } from '@apollo/client';
import { Assignment, ChevronLeft, Info, PendingActions } from '@mui/icons-material';
import { Grid, Paper, Tab, Tabs } from '@mui/material';
import { ModuleSelection } from '@thro-types/modules-selections';

import { ReactNode, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';

import QUERY_LOTTERY_RESULT from 'gql/queries/ModuleSelectionsService/getLatestCalculatedLotteryResultForStudent.gql';
import MODULES_SELECTION_QUERY from 'gql/queries/ModulesService/getModuleSelectionById.gql';
import USER_QUERY from 'gql/queries/currentUser.gql';

import { BackToTopButton } from 'app/components/BackToTopButton';
import GQLGuard from 'app/components/GQLGuard';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { LatestCalculatedLotteryResultsForStudent } from 'types/AppTypes';
import usePageHeading from 'utils/hooks/usePageHeading';
import useQueryStringState from 'utils/hooks/useQueryStringState';

import { hasAtLeastDischargable } from '../../ModuleSelections/helper';
import { SubjectType } from '../../ModuleSelections/models';
import { ModuleStudentSelection } from '../ModuleSelection';
import { PreviewTab } from './Components/previewTab';
import { ReceivedModules } from './Components/receivedModules';
import { WaitingList } from './Components/waitingList';

export enum StudentTabPanels {
  SELECTION_INFORMATION = 'selectionInformation',
  RECEIVED_MODULES = 'receivedModules',
  WAITING_LIST = 'waitingList'
}

interface ModuleSelectionStudentDetailViewProps {
  subjectType: SubjectType;
}

export const ModuleSelectionStudentDetailView = (props: ModuleSelectionStudentDetailViewProps) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const client = useApolloClient();
  const { currentUser } = client.readQuery({ query: USER_QUERY });
  const [tab, setTab] = useQueryStringState<StudentTabPanels>(
    'tab',
    StudentTabPanels.SELECTION_INFORMATION
  );
  const getModuleSelectionById = useQuery<any, any>(MODULES_SELECTION_QUERY, {
    context: { clientName: 'federation' },
    variables: { id: parseInt(id!, 10) },
    fetchPolicy: 'no-cache'
  });

  const getLatestLottery = useQuery<any, any>(QUERY_LOTTERY_RESULT, {
    context: { clientName: 'federation' },
    variables: {
      studentId: parseInt(currentUser.id),
      moduleSelectionId: parseInt(getModuleSelectionById.data?.getModuleSelectionById.id)
    },
    skip:
      getModuleSelectionById.loading ||
      !hasAtLeastDischargable(getModuleSelectionById.data?.getModuleSelectionById)
  });

  usePageHeading(
    {
      heading:
        getModuleSelectionById?.data?.getModuleSelectionById?.title ||
        t(translations.common.loading()),
      headingProps: {
        back:
          location.state?.back ?? `/${props.subjectType.toLowerCase()}/student/module-selections`
      }
    },
    [getModuleSelectionById?.data]
  );

  useEffect(() => {
    // ? This is required for the back button to work on the mobile app. Without it, the content is being rendered white on back button press
    window.history.scrollRestoration = 'manual';
  }, []);

  function renderTabs(moduleSelection: ModuleSelection, receivedDesiredNumber?: boolean) {
    const tabs: ReactNode[] = [];

    if (isMobile) {
      tabs.push(
        <Tab
          value={undefined}
          icon={isMobile ? <ChevronLeft /> : undefined}
          label={t(translations.common.back())}
          style={isMobile ? { fontSize: 10, flex: 1 } : undefined}
          onClick={() => navigate(`/${props.subjectType.toLowerCase()}/student/module-selections`)}
        />
      );
    }

    tabs.push(
      <Tab
        value={StudentTabPanels.SELECTION_INFORMATION}
        icon={isMobile ? <Info /> : undefined}
        label={
          isMobile
            ? t(translations.pages.studentView.tabs.selectionInformationMobile())
            : t(translations.pages.studentView.tabs.selectionInformation())
        }
        style={isMobile ? { fontSize: 10, flex: 1 } : undefined}
      />
    );

    if (!moduleSelection.publishedAt) {
      return tabs;
    }

    tabs.push(
      <Tab
        disabled={!moduleSelection.publishedAt}
        value={StudentTabPanels.RECEIVED_MODULES}
        icon={isMobile ? <Assignment /> : undefined}
        label={
          isMobile
            ? t(translations.pages.studentView.tabs.receivedModulesMobile())
            : t(translations.pages.studentView.tabs.receivedModules())
        }
        style={isMobile ? { fontSize: 10, flex: 1 } : undefined}
      />
    );

    if (!receivedDesiredNumber) {
      tabs.push(
        <Tab
          disabled={!moduleSelection.publishedAt}
          value={StudentTabPanels.WAITING_LIST}
          icon={isMobile ? <PendingActions /> : undefined}
          label={t(translations.pages.studentView.tabs.waitingList())}
          style={isMobile ? { fontSize: 10, flex: 1 } : undefined}
        />
      );
    }

    return tabs;
  }

  const Body = () => (
    <GQLGuard
      query={[getModuleSelectionById, getLatestLottery]}
      onError={error => {
        if (error?.graphQLErrors[0]?.extensions?.status === 404) {
          dispatch(
            AlertActions.error(`${t(translations.pages.moduleSelectionsDetail.failedLoading())}`)
          );
        } else {
          dispatch(
            AlertActions.error(
              `${t(translations.pages.moduleSelectionsDetail.failedLoading())}: ${error?.message}`
            )
          );
        }
        navigate('/', { replace: true });
        return null;
      }}
    >
      {([getModuleSelectionById, getLatestLottery]) => {
        const moduleSelection = getModuleSelectionById.getModuleSelectionById;
        const latestLottery: LatestCalculatedLotteryResultsForStudent | undefined =
          getLatestLottery?.getLatestCalculatedLotteryResultForStudent;
        return (
          <Grid>
            {(hasAtLeastDischargable(moduleSelection) || isMobile) && (
              <Paper className="selection-detail" square={true}>
                <Tabs
                  value={tab}
                  onChange={(_event, value: StudentTabPanels) => setTab(value)}
                  aria-label="preview/occupancy/studentlist tabs"
                  indicatorColor="primary"
                >
                  {renderTabs(moduleSelection, latestLottery?.receivedDesiredNumber)}
                </Tabs>
              </Paper>
            )}
            <TabPanel value={tab} index={StudentTabPanels.SELECTION_INFORMATION}>
              <PreviewTab
                moduleSelection={new ModuleStudentSelection(moduleSelection)}
                subjectType={props.subjectType}
              />
            </TabPanel>
            {hasAtLeastDischargable(moduleSelection) &&
              moduleSelection.publishedAt &&
              latestLottery && (
                <>
                  <TabPanel value={tab} index={StudentTabPanels.RECEIVED_MODULES}>
                    <ReceivedModules
                      moduleSelection={new ModuleStudentSelection(moduleSelection)}
                      latestLottery={latestLottery}
                    />
                  </TabPanel>
                  <TabPanel value={tab} index={StudentTabPanels.WAITING_LIST}>
                    <WaitingList
                      moduleSelection={new ModuleStudentSelection(moduleSelection)}
                      latestLottery={latestLottery}
                    />
                  </TabPanel>
                </>
              )}
          </Grid>
        );
      }}
    </GQLGuard>
  );

  return (
    <>
      <Body />
      {isMobile && <BackToTopButton threshold={700} />}
    </>
  );
};

const TabPanel = props => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
};
